import React, { useState, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import Loader from '@/modules/base/components/loader/loader'
import PropTypes from 'prop-types'

import { getUploader } from '@/modules/file/uploader'
import DropZoneUploader from '@/modules/base/components/file-uploader/drop-zone-uploader/drop-zone-uploader'
import downloadIcon from '@assets/images/icons/download.svg'
import IconFile from '@assets/images/icons/fileImport.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { SubTitle, Title } from '@/modules/base/components/texts/index'
import * as DataImportHelpers from '../_helpers/data-import-helpers'

import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import { read } from 'xlsx'
import lang from '@/lang'
import { IMPORT_TYPE_CUSTOMERS, IMPORT_TYPE_SUPPLIERS, IMPORT_TYPE_ARTICLES } from '@/modules/import/mass-import/mass-import-service'
import { SUBSCRIPTION_MODULES as subscriptionModules } from '@/modules/base/util/constants'

import { toast } from '@/modules/base/components/toast/toastConfig'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'

const { massImport } = lang

const DataImportInput = (props) => {
  const classes = useStyles()
  const {
    onDownloadTemplate,
    allowedExtensions,
    showDownloadTemplate,
    onLoad,
    label,
    type,
    icon,
    skipTransformation,
  } = props
  const planIncludesCashRegister = useCheckModuleAccess(subscriptionModules.CASH_REGISTER)
  const planIncludesStockmanagement = useCheckModuleAccess(subscriptionModules.STOCKMANAGEMENT)

  const maxDiscountInPercent = '100'

  const [isLoading, setIsLoading] = useState(false)

  const transformWorkBook = (binaryStr) => {
    const workBook = read(binaryStr, { type: 'binary' })
    const firstSheetName = workBook.SheetNames[0]
    const worksheet = workBook.Sheets[firstSheetName]

    // find header columns
    const cells = Object.keys(worksheet)
    const { columns, rows } = cells.reduce((result, current) => {
      const expr = /([A-Z]+)(\d+)/
      const match = expr.exec(current)
      if (match && match.length > 0) {
        const rowIndex = match[2]
        if (rowIndex === '1') {
          result.columns[match[1]] = worksheet[match[0]].w.toLowerCase()
        } else {
          if (!result.rows[rowIndex]) {
            result.rows[rowIndex] = []
          }
          result.rows[rowIndex][match[1]] = worksheet[match[0]].v ? worksheet[match[0]].v : ''
        }
      }
      return result
    }, { columns: {}, rows: [] })

    const rowData = rows.map((row, index) => {
      const resultRow = []
      let isEmptyResultRow = 0

      // filter out empty rows and those coming from the export
      // and give back the results
      Object.keys(columns).forEach(function(column) {
        resultRow[columns[column]] = row[column] ? row[column] : ''
        resultRow[columns[column]] !== '' && isEmptyResultRow++
      })

      if (isEmptyResultRow !== 0) { return resultRow } else { return null }
    }).filter((row) => {
      let returnedRow = null
      if (row !== null) {
        returnedRow = row
      }
      if (row['kunden-nr.'] !== undefined) {
        returnedRow = typeof row['kunden-nr.'] === 'string' && row['kunden-nr.'].search('Anzahl') !== -1 ? null : row
      } else {
        returnedRow = row
      }
      return returnedRow !== null
    })

    return {
      columns: Object.values(columns).map(column => { return { column, alias: column } }),
      rowData,
    }
  }

  /**
   * Transforms a given data import and tries to match fields (for display in the table)
   * @param {object} data - contains both headers and row data
   * @returns {object} - updated data object
   */
  const transformAsCustomerData = (data) => {
    const columnData = DataImportHelpers.updateColumns(data.columns)
    const rowData = data.rowData

    rowData.map((row) => {
      const importFromWindows = row.art === undefined
      const customerKind = DataImportHelpers.defineCustomerKind({ kundenart: row.kundenart, vorname: row.vorname, art: row.art }, importFromWindows)
      const discount = DataImportHelpers.sanitizeDiscountForTableView(maxDiscountInPercent, [row.rabatt, row['rabatt in %']])
      const lastNameOrCompanyName = row.firmenname || row['nachname/firma'] || row['nachname firma'] || row.nachnamefirma || row.nachname

      row['kunden-nr.'] = DataImportHelpers.sanitizeCustomerNumberForTableView([row.kundennummer, row['kunden-nr.']])
      customerKind === 'privat' ? row.nachname = lastNameOrCompanyName : row.firmenname = lastNameOrCompanyName
      customerKind === 'privat' ? row.art = 'Person' : row.art = 'Firma'
      row.rabatt = discount

      return rowData
    })
    return {
      columns: columnData,
      rowData,
    }
  }

  /**
   * Transforms a given data import and tries to match fields (for display in the table)
   * @param {object} data - contains both headers and row data
   * @returns {object} - updated data object
   */
  const transformAsSupplierData = (data) => {
    const columnData = DataImportHelpers.updateColumns(data.columns)
    const rowData = data.rowData

    rowData.map((row) => {
      const discount = DataImportHelpers.sanitizeDiscountForTableView(maxDiscountInPercent, [row.rabatt, row['rabatt in %']])

      row.rabatt = discount
      row.name = row.firmenname
      row.vorname = row['vorname ansprechpartner']
      row.nachname = row['nachname ansprechpartner']

      return rowData
    })
    return {
      columns: columnData,
      rowData,
    }
  }

  const onError = (id, name, errorReason, xhr) => {
    toast.showToast({ type: 'error', message: errorReason })

    setIsLoading(false)
  }

  const onSubmitted = (id, name) => {
    setIsLoading(true)

    const file = uploader.methods.getFile(id)
    const reader = new FileReader()
    reader.onabort = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onerror = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onload = () => {
      // Do whatever you want with the file contents
      const data = skipTransformation ? reader.result : transformWorkBook(reader.result)
      const fileName = name
      switch (type) {
        case IMPORT_TYPE_CUSTOMERS:
          onLoad({ data: transformAsCustomerData(data), fileName })
          break

        case IMPORT_TYPE_SUPPLIERS:
          onLoad({ data: transformAsSupplierData(data), fileName })
          break

        default:
          onLoad({ data, fileName })
          break
      }
    }
    reader.readAsBinaryString(file)
  }

  const uploader = getUploader({
    autoUpload: false,
    messages: {
      typeError: lang.fileUploaderTypesError.replace('%s', allowedExtensions.join(',')),
    },
    request: {
    },
    validation: {
      allowedExtensions,
    },
    callbacks: {
      onError,
      onSubmitted,
    },
  })

  // show an error toast in case some import features require plan upgrades
  // currently CASH_REGISTER and STOCKMANAGEMENT
  const checkFieldAvailability = () => {
    let msg = ''
    if (!planIncludesCashRegister && !planIncludesStockmanagement) {
      msg = lang.massImport.fieldsIgnored.article.noPOSNoStockManagement
    } else if (!planIncludesCashRegister && planIncludesStockmanagement) {
      msg = lang.massImport.fieldsIgnored.article.noPOS
    } else if (planIncludesCashRegister && !planIncludesStockmanagement) {
      msg = lang.massImport.fieldsIgnored.article.noStockManagement
    }

    return (msg)
  }

  if (type === IMPORT_TYPE_ARTICLES) {
    useEffect(() => {
      const fieldAvailabilityMessage = checkFieldAvailability()
      fieldAvailabilityMessage !== '' && toast.showToast({ type: 'info', message: fieldAvailabilityMessage })
    }, [])
  }

  const templateDownloadButton = showDownloadTemplate
    ? (
      <ButtonIconSquare className={classes.downloadLink} icon={downloadIcon} label={massImport.templateDescription} />
    )
    : null

  return (
    <div className={classes.container}>
      <Loader text={massImport.importing} visible={isLoading} />
      <DropZoneUploader uploader={uploader} accept={allowedExtensions} multiple={false} className={classes.dropZone} classNameInput={classes.fileInput} >
        <div className={classes.iconWrapper}>
          <SVGIcon svg={icon} customClass={classes.icon} height='32px' width='32px' />
        </div>
        <div className={classes.center}>
          <Title position="center">{label}</Title>
          <SubTitle customClass={classes.subTitle}>{lang.massImport.dragLabelA}<a>{lang.massImport.dragLabelB}</a></SubTitle>
          <SubTitle customClass={classes.subTitle}>
            {lang.massImport.templateA}
            <a onClick={onDownloadTemplate} className={classes.templateLink}>
              <SVGIcon svg={IconFile} customClass={classes.templateIcon} height='13px' width='13px' />
              {lang.massImport.templateB}
            </a>
            {lang.massImport.templateC}
          </SubTitle>
        </div>
      </DropZoneUploader>
      {templateDownloadButton}
    </div>
  )
}

DataImportInput.propTypes = {
  onDownloadTemplate: PropTypes.func,
  allowedExtensions: PropTypes.array,
  showDownloadTemplate: PropTypes.bool,
  onLoad: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  icon: PropTypes.any,
  skipTransformation: PropTypes.bool,
}

DataImportInput.defaultProps = {
  skipTransformation: false,
  showDownloadTemplate: true,
  allowedExtensions: ['xlsx'],
}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 870,
    position: 'relative',
    fill: `${theme.primary} !important`,
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 10,
  },
  downloadLink: {
    position: 'absolute',
    '& .download0, & .download1': { // für mb
      fill: theme.white,
    },
    bottom: 37,
    right: 40,
    transition: 'all ease-in-out .2s',
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#fff',
    height: '77px',
    width: '77px',
    marginTop: 32,
    borderRadius: '50%',
    border: `1px dashed ${theme.primary}`,
    '& ~icon *': {
      color: theme.primary,
      fill: theme.primary,
    },
  },
  subTitle: {
    textAlign: 'center',
    lineHeight: 1.8,
  },
  templateLink: {
    position: 'relative',
    paddingLeft: 16,
    zIndex: 1,
  },
  templateIcon: {
    position: 'absolute',
    top: 1,
    left: 0,
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    height: 240,
    '&.dropzoneActive *, &:hover  *': {
      outlineColor: theme.primary,
      borderColor: theme.primary,
      fill: theme.primary,
    },
    '&.dropzoneActive + $downloadLink': {
      opacity: 0,
    },
    '& $fileInput': {
      '& *': {
        fill: theme.primary,
      },
    },
  },
  fileInput: {
    display: 'flex !important',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'transparent',
    '&:hover': {
      background: 'transparent !important',
    },
  },
}))

export default DataImportInput
